import { type ReactNode, useId } from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';

import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { InfoTooltip } from '../../../shared/ui/forms/InfoTooltip';
import { VerticalDivider } from '../../../shared/ui/VerticalDivider';

export interface ConfigFieldProps {
  readonly label: string;
  /** Info-tooltip help text shown next to the label. */
  readonly info: string;
  /** Accessible label for the info-icon trigger. */
  readonly infoAltText: string;
  readonly isRequired?: boolean;
  readonly children?: ReactNode;
  /**
   * Alternative to `children` for controls that need the label element's id
   * (e.g. a Slider wired via `aria-labelledby`). Receives the generated id.
   */
  readonly renderLabel?: (labelId: string) => ReactNode;
}

/**
 * One labelled configuration field on the Question Configuration step: a label
 * row (`RequiredLabel` + `VerticalDivider` + `InfoTooltip`) above its control.
 * When the control needs the label's element id, pass `renderLabel` to receive
 * it; otherwise pass the control as `children`. Shared by the Basic
 * Information and Documentation cards so the label-row structure lives in one
 * place.
 */
export function ConfigField({
  label,
  info,
  infoAltText,
  isRequired = false,
  children,
  renderLabel,
}: ConfigFieldProps): JSX.Element {
  const labelId = useId();
  return (
    <Col gridGap="dimensions.spacing.100">
      <Row alignItems="center" gridGap="dimensions.spacing.200">
        {/* id lives on the label text only (not the whole Row): a control
         *  wired via aria-labelledby={labelId} must resolve to just the field
         *  label, not the label PLUS the info-icon's aria-label. The asterisk
         *  inside RequiredLabel is aria-hidden, so it's excluded from the name. */}
        <View id={labelId}>
          <RequiredLabel isRequired={isRequired} hasError={false}>
            {label}
          </RequiredLabel>
        </View>
        <VerticalDivider />
        <InfoTooltip text={info} ariaLabel={infoAltText} />
      </Row>
      {renderLabel ? renderLabel(labelId) : children}
    </Col>
  );
}
